import { Dispatch, SetStateAction, useMemo, useState } from "react";
import Button from "app/storybookComponents/Button";
import GoalTypeSelector from "./GoalTypeSelector";
import { CustomVisibilityEntities, TGoalForm } from "../../types";
import GoalVisibilitySelector from "./GoalVisibilitySelector";
import { cn } from "app/lib/utils";
import { Separator } from "app/shadcnComponents/ui/separator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import GoalBestPracticeDrawer from "./GoalBestPracticeDrawer";
import ParentGoalSelectorDrawer from "./ParentGoalSelectorDrawer";

const maxChars = 250;

interface Props {
  onBack: () => void;
  onSave: () => void;
  isOrgGoal?: boolean;
  isLoading?: boolean;
  saveGoalButtonText?: string;
  form: TGoalForm;
  onFormChange: (form: TGoalForm) => void;
  customVisibilityEntities: CustomVisibilityEntities;
  onChangeCustomVisibilityEntities: Dispatch<
    SetStateAction<CustomVisibilityEntities>
  >;
  selectedUserId: number | null;
  wizardStep: number;
  onWizardStepChange: (step: number) => void;
}

const GoalWizard = ({
  onBack,
  isOrgGoal,
  onSave,
  isLoading,
  saveGoalButtonText = "Save Goal",
  form,
  onFormChange,
  customVisibilityEntities,
  onChangeCustomVisibilityEntities,
  wizardStep,
  onWizardStepChange,
  selectedUserId,
}: Readonly<Props>) => {
  const [isBestPracticeDrawerOpen, setIsBestPracticeDrawerOpen] =
    useState(false);
  const minDate = useMemo(() => new Date().toISOString().split("T")[0], []);

  const handleFieldChange = (field: keyof TGoalForm, value: string) => {
    onFormChange({ ...form, [field]: value });
  };

  const handleParentGoalSelect = (parentGoalId: number | null) => {
    onFormChange({ ...form, parentGoalId });
  };

  const onNext = () => {
    if (wizardStep >= 2) {
      onSave();
      return;
    }
    onWizardStepChange(wizardStep + 1);
  };

  const onPrevious = () => {
    if (wizardStep <= 1) {
      onBack();
      return;
    }
    onWizardStepChange(wizardStep - 1);
  };

  const getStepButton = (
    stepNumber: number,
    label: string,
    state: "active" | "done" | "pending"
  ) => {
    const buttonClassMap = {
      active: "bg-sapphire-10 text-sapphire-100",
      done: "bg-success-green-10 text-success-green-100",
      pending: "bg-charcoal-05 text-charcoal-60",
    };

    const stepButtonClass = {
      active: "bg-sapphire-100 text-white",
      done: "bg-success-green-100 text-white",
      pending: "bg-charcoal-20 text-charcoal-80",
    };

    return (
      <button
        onClick={() => {
          if (state === "pending") {
            return;
          }
          onWizardStepChange(stepNumber);
        }}
        className={cn(
          "rounded-full d-flex text-sm px-2 py-1 align-items-center gap-2",
          buttonClassMap[state]
        )}
      >
        <div
          className={cn(
            "rounded-full w-6 h-6 flex items-center justify-center",
            stepButtonClass[state]
          )}
        >
          {state === "done" ? <FontAwesomeIcon icon={faCheck} /> : stepNumber}
        </div>
        <div>{label}</div>
      </button>
    );
  };

  const getBody = () => {
    if (wizardStep === 1) {
      const availableTypes = ["work", "team"];
      if (!isOrgGoal) {
        availableTypes.push("personal");
      }
      return (
        <>
          <div className="text-charcoal-60 text-sm">
            All fields marked with <span className="text-red-500">*</span> are
            mandatory.
          </div>
          <GoalTypeSelector
            value={form.type ?? ""}
            onChange={(value) => handleFieldChange("type", value)}
            availableTypes={availableTypes}
          />
          <div className="gm-field">
            <p className="text-form-prompt font-medium text-primary-text">
              What's your goal? (Objective){" "}
              <span className="text-red-500">*</span>
            </p>

            <textarea
              name="title"
              className="gm-textarea"
              placeholder="Enter your goal here…"
              maxLength={maxChars}
              value={form.title}
              onChange={(e) => handleFieldChange("title", e.target.value)}
            />
            <div className="gm-char-count-left">
              {form.title.length}/{maxChars} characters
            </div>
          </div>

          <div className="gm-field">
            <p className="text-form-prompt font-medium text-primary-text">
              How will you know if you've achieved this?
            </p>
            <textarea
              name="successCriteria"
              className="gm-textarea"
              placeholder="Enter KPIs or key results…"
              maxLength={maxChars}
              value={form.successCriteria}
              onChange={(e) =>
                handleFieldChange("successCriteria", e.target.value)
              }
            />
            <div className="gm-char-count-left">
              {form.successCriteria.length}/{maxChars} characters
            </div>
          </div>

          <div className="gm-field">
            <p className="text-form-prompt font-medium text-primary-text">
              When will you complete this goal? (Key Results){" "}
              <span className="text-red-500">*</span>
            </p>

            <input
              type="date"
              name="dueDate"
              id="dueDate"
              className="goal-input"
              value={form.dueDate}
              min={minDate}
              onChange={(e) => handleFieldChange("dueDate", e.target.value)}
            />
          </div>
        </>
      );
    }

    if (wizardStep === 2) {
      return (
        <>
          <GoalVisibilitySelector
            value={form.visibility}
            onChange={(value) => onFormChange({ ...form, visibility: value })}
            isOrgGoal={isOrgGoal}
            onChangeCustomVisibilityEntities={onChangeCustomVisibilityEntities}
            customVisibilityEntities={customVisibilityEntities}
            goalsUserAccountId={selectedUserId}
          />
          {!isOrgGoal && selectedUserId && (
            <ParentGoalSelectorDrawer
              effectiveUserId={selectedUserId}
              onSelectParentGoal={handleParentGoalSelect}
              selectedParentGoalId={form.parentGoalId}
            />
          )}
        </>
      );
    }
    return null;
  };

  const isActionButtonDisabled = () => {
    if (isLoading) {
      return true;
    }
    if (wizardStep === 1) {
      return !form.title || !form.dueDate;
    }
    return false;
  };

  return (
    <>
      <GoalBestPracticeDrawer
        isOpen={isBestPracticeDrawerOpen}
        onClose={() => {
          setIsBestPracticeDrawerOpen(false);
        }}
      />
      <div className="d-flex gap-2 px-lg">
        {getStepButton(1, "Your Goal", wizardStep <= 1 ? "active" : "done")}
        {getStepButton(2, "Settings", wizardStep >= 2 ? "active" : "pending")}
      </div>
      <Separator />
      <div className="p-lg gap-lg d-flex flex-column pt-0">{getBody()}</div>
      <Separator />
      <div className="d-flex justify-content-between p-lg pt-0">
        <div>
          <Button
            variant="secondary-blue"
            className="border-0"
            onClick={() => setIsBestPracticeDrawerOpen(true)}
          >
            <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
            Learn more about goals
          </Button>
        </div>
        <div className="d-flex gap-2">
          {wizardStep > 1 ? (
            <Button
              onClick={onPrevious}
              disabled={isLoading}
              variant="secondary-gray"
            >
              Back
            </Button>
          ) : null}
          <Button onClick={onNext} disabled={isActionButtonDisabled()}>
            {wizardStep < 2 ? "Next" : saveGoalButtonText}
          </Button>
        </div>
      </div>
    </>
  );
};

export default GoalWizard;
